import React, { useCallback, useState, useEffect, useMemo, useContext, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useAnnotationsDispatch } from '@confluence/annotation-provider-store/entry-points/AnnotationsContext.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { withDialogs } from '@confluence/dialogs/entry-points/withDialogs.ts';
import { type DialogsStateContainer } from '@confluence/dialogs/entry-points/DialogsStateContainer.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { getEditorAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getEditorAnnotationEventEmitter.ts';
import {
	handleKeyboardShortcutNavigation,
	ScrollBehavior,
	NavigationContext,
	ShortcutType,
} from '@confluence/comments-util/entry-points/commentsNavigationUtils.ts';
import { getNextUnreadThreadKey } from '@confluence/comments-util/entry-points/domUtils.ts';
import { openInlineCommentInEditor } from '@confluence/comments-util/entry-points/editorAnnotations.ts';
import {
	useObjectCommentDispatchContext,
	useObjectCommentContext,
	AddFooterCommentMode,
} from '@confluence/comment-context/entry-points/ObjectCommentContext.ts';
import {
	useInlineCommentsContext,
	useInlineCommentsDispatchContext,
} from '@confluence/comment-context/entry-points/InlineCommentsContext.ts';
import { useActiveCommentsQuery } from '@confluence/comments-data/entry-points/useActiveCommentsQuery.ts';
import {
	CommentType,
	useCommentsDataState,
	FooterCommentsLoadingState,
	type ReplyData as LegacyReplyData,
} from '@confluence/comments-data/entry-points/useCommentsData.ts';
import type { ReplyData as NewReplyData } from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils.ts';
import { useProcessedCommentsDataState } from '@confluence/comments-data-manager/entry-points/useProcessedCommentsData.ts';
import { expVal } from '@confluence/feature-experiments';
import { useCreateCommentAction } from '@confluence/highlight-actions/entry-points/useCreateCommentAction.ts';
import { useInlineCommentQueryParams } from '@confluence/comment/entry-points/useCommentQueryParams.ts';
import { GeneralShortcutListener } from '@confluence/shortcuts/entry-points/GeneralShortcutListener.ts';
import {
	NEXT_COMMENT_SHORTCUT,
	PREV_COMMENT_SHORTCUT,
	UNIVERSAL_PREV_COMMENT_SHORTCUT,
	UNIVERSAL_NEXT_COMMENT_SHORTCUT,
	UNIVERSAL_NEXT_UNREAD_COMMENT_SHORTCUT,
	UNIVERSAL_OPEN_COMMENT_EDITOR,
	UNIVERSAL_SELECT_COMMENT_SHORTCUT,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import { handlePanelSelectionAndScroll } from '@confluence/comments-panel/entry-points/commentThreadHelper.ts';
import { useCommentsPanelScroll } from '@confluence/comments-panel-utils/entry-points/useCommentsPanelScroll.ts';
import {
	useCommentsPanel,
	useCommentsPanelState,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { useObjectSidebarState } from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';
import { PanelName } from '@confluence/object-sidebar-api/entry-points/PanelName.ts';
import { fg } from '@confluence/feature-gating';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import type { FullPageEditorPresetAPI } from '@confluence/editor-presets/entry-points/useFullPageEditorPreset.ts';
import { VIEW_INLINE_COMMENT_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import { useAddCommentPermissionCheck } from '@confluence/comments-hooks/entry-points/useAddCommentPermissionCheck.ts';
import { usePageClass } from '@confluence/comments-hooks/entry-points/usePageClass.ts';
import { useUnreadCommentsState } from '@confluence/unread-comments/entry-points/useUnreadComments.ts';
import { createInlineCommentCompoundExperience } from '@confluence/experience-tracker/entry-points/createInlineCommentCompoundExperience.ts';
import { getCommentsDataManagerExperimentAttributes } from '@confluence/experience-tracker/entry-points/getCommentsDataManagerExperimentAttributes.ts';

import {
	parsePanelAndInlineRefs,
	getNextRef,
	isNavigatingToPotentiallyUnloadedFooterComment,
} from './helper/commentsShortcutsHelper.ts';
import { useFooterComments } from './useFooterComments.ts';

const EditorCommentShortcutListenerComponent = ({
	contentId,
	viewMode,
	dialogs,
	editorApi,
}: {
	contentId?: string;
	viewMode?: boolean;
	dialogs: DialogsStateContainer;
	editorApi?: FullPageEditorPresetAPI;
}) => {
	const { navigateToInlineComment, setActiveHighlight } = useInlineCommentsDispatchContext();
	const pageMode = useGetPageMode();
	const {
		orderedActiveAnnotationIdList,
		commentsDataMap: legacyCommentsDataMap,
		footerCommentsLoadingState,
	} = useCommentsDataState();

	// Get processed comments data for new comments data manager
	const processedCommentsData = useProcessedCommentsDataState();

	const commentsDataMap = expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
		? processedCommentsData.commentsDataMap
		: legacyCommentsDataMap;

	const { removeCommentQueryParams } = useInlineCommentQueryParams();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { activeHighlight } = useInlineCommentsContext();
	const [isNextCommentLoading, setIsNextCommentLoading] = useState(false);
	const [alreadyFetchedInitialData, setAlreadyFetchedInitialData] = useState(false);
	const [triggeredByUnreadKeyboardShortcut, setTriggeredByUnreadKeyboardShortcut] = useState(false);

	// Used to facilitate loading footer comments and navigating after load
	// Should only be set when confluence_frontend_kb_footer_loading is true and pageMode == PageMode.LIVE
	const [pendingStandaloneAction, setPendingStandaloneAction] = useState<
		('next' | 'previous') | null
	>(null);

	const [
		{ currentlyRenderedThreads, currentlySelectedThreadKey },
		{ setCurrentlySelectedThreadKey },
	] = useCommentsPanel();
	const { scrollCommentsPanelToThread } = useCommentsPanelScroll();

	// Track previous annotation for difference-based scrolling
	const previousAnnotationRef = useRef<string | undefined>(undefined);

	// Update ref whenever activeHighlight changes
	useEffect(() => {
		// Only update if there's a valid value (not empty string or null)
		if (activeHighlight) {
			previousAnnotationRef.current = activeHighlight;
		}
	}, [activeHighlight]);

	const { isObjectSidebarShown, panel } = useObjectSidebarState();
	const emitter = getEditorAnnotationEventEmitter();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const pageClass = usePageClass();

	const { expandCommentContainer, setFooterEditorEntry } = useObjectCommentDispatchContext();
	const { expanded, isObjectCommentWrapperVisible, footerEditorEntry } = useObjectCommentContext();
	const { addFooterCommentAdf } = useCommentsPanelState();
	const { unreadCommentIDSet } = useUnreadCommentsState();
	const { canAddComments } = useAddCommentPermissionCheck(contentId ?? '');

	const orderedActiveAnnotations = useMemo(() => {
		return orderedActiveAnnotationIdList.map((a) => a.threadKey);
	}, [orderedActiveAnnotationIdList]);

	useEffect(() => {
		setIsNextCommentLoading(false);
	}, [activeHighlight, currentlySelectedThreadKey]);

	useEffect(() => {
		return () => {
			setCurrentlySelectedThreadKey('');
		};
	}, [setCurrentlySelectedThreadKey]);

	const { footerCommentThreads, footerCommentIds } = useFooterComments();
	const { setEditorViewProps } = useAnnotationsDispatch();

	const { unreadInlineComments, unreadGeneralComments } = useMemo(() => {
		const unreadInlineComments: string[] = [];
		const unreadGeneralComments: string[] = [];

		orderedActiveAnnotations.forEach((threadKey) => {
			const commentData = commentsDataMap.inline[threadKey];
			if (commentData) {
				let isCommentUnread = false;
				let hasUnreadReplies = false;

				if (fg('confluence_frontend_unread_comments_fixes')) {
					// Use unreadCommentIDSet when feature flag is enabled
					// in SSR, unreadCommentIDSet is undefined
					isCommentUnread = unreadCommentIDSet?.has(commentData.id);
					hasUnreadReplies =
						commentData.replies?.some((reply) => reply && unreadCommentIDSet?.has(reply.id)) ||
						false;
				} else {
					// Use legacy .isUnread field when feature flag is disabled
					isCommentUnread = commentData.isUnread || false;
					hasUnreadReplies =
						commentData.replies?.some(
							(reply: LegacyReplyData | NewReplyData | null | undefined) => reply && reply.isUnread,
						) || false;
				}

				if (isCommentUnread || hasUnreadReplies) {
					unreadInlineComments.push(threadKey);
				}
			}
		});
		Object.keys(commentsDataMap.general).forEach((threadKey) => {
			const commentData = commentsDataMap.general[threadKey];
			if (commentData) {
				let isCommentUnread = false;
				let hasUnreadReplies = false;

				if (fg('confluence_frontend_unread_comments_fixes')) {
					// Use unreadCommentIDSet when feature flag is enabled
					isCommentUnread = unreadCommentIDSet?.has(commentData.id);
					hasUnreadReplies =
						commentData.replies?.some((reply) => reply && unreadCommentIDSet?.has(reply.id)) ||
						false;
				} else {
					// Use legacy .isUnread field when feature flag is disabled
					isCommentUnread = commentData.isUnread || false;
					hasUnreadReplies =
						commentData.replies?.some(
							(reply: LegacyReplyData | NewReplyData | null | undefined) => reply && reply.isUnread,
						) || false;
				}

				if (isCommentUnread || hasUnreadReplies) {
					unreadGeneralComments.push(threadKey);
				}
			}
		});

		return {
			unreadInlineComments,
			unreadGeneralComments,
		};
	}, [commentsDataMap, orderedActiveAnnotations, unreadCommentIDSet]);

	const sendKeyboardShortcutAnalytics = (shortcut: string) => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'editPage',
				action: 'pressed',
				actionSubject: 'keyboardShortcut',
				actionSubjectId: 'createComment',
				attributes: {
					key: shortcut,
				},
			},
		}).fire();
	};

	const { handleCommentButtonClick } = useCreateCommentAction({
		contentId: contentId || '',
		isFabric: true,
		sendKeyboardShortcutAnalytics,
	});

	const onNavigationClick = useCallback(
		(nextMarkerRef?: string) => {
			const previousAnnotationId = previousAnnotationRef.current;
			setCurrentlySelectedThreadKey('');
			if (!nextMarkerRef) return;

			emitter.emit('setselectedannotation', nextMarkerRef);
			const commentElement = document.getElementById(nextMarkerRef);
			if (commentElement) {
				/*
				 * Don't start the experience if the create sidebar is shown because we can't predict
				 * if the user will close the create sidebar to continue the navigation or create a
				 * new comment. If the user continues the navigation, the experience will be started
				 * by the annotation-provider-inline-comments/editor/ViewManager entrypoint.
				 */
				if (
					document.querySelector('[data-testid="editor-create-sidebar"]') === null &&
					(pageClass === 'renderer' || pageClass === 'editor') &&
					fg('confluence_frontend_view_inline_comment_keyboard')
				) {
					experienceTracker.start({
						name: VIEW_INLINE_COMMENT_EXPERIENCE,
						timeout: ExperienceTimeout.VIEW_INLINE_COMMENT,
						attributes: {
							...getCommentsDataManagerExperimentAttributes(),
							annotationId: nextMarkerRef,
							entryPoint: 'comments-shortcuts/EditorCommentShortcutListener',
							pageClass: 'editor',
						},
					});
				}
				if (fg('confluence_comments_unified_navigation')) {
					handleKeyboardShortcutNavigation({
						scrollBehavior: ScrollBehavior.SMOOTH,
						context: NavigationContext.EDITOR,
						block: 'center',
						annotationId: nextMarkerRef,
						previousAnnotationId,
						commentType: 'INLINE',
					});
				} else {
					openInlineCommentInEditor({
						targetElement: commentElement,
						targetElementID: nextMarkerRef,
					});
				}
			}
		},
		[emitter, setCurrentlySelectedThreadKey, experienceTracker, pageClass],
	);

	const scrollToFooterComment = useCallback(
		(threadKey: string) => {
			// Reset previous annotation ref when navigating to footer comment
			previousAnnotationRef.current = undefined;
			// Close inline comment when keyboard navigating from inline comment to footer comment
			// TODO: The logic to close inline comment is from ViewManager.tsx. To revisit - create a shared function so the close inline comment logic can be reused
			if (!fg('confluence_comments_unified_navigation')) {
				setTimeout(() => {
					// Remove focus from the currently focused element only if unified navigation is enabled
					editorApi?.core.actions.focus();
				}, 100);
			}
			emitter.emit('closeinlinecomment');
			setEditorViewProps(null);

			setCurrentlySelectedThreadKey(threadKey);
			if (fg('confluence_comments_unified_navigation')) {
				handleKeyboardShortcutNavigation({
					scrollBehavior: ScrollBehavior.SMOOTH,
					block: 'center',
					annotationId: threadKey,
					commentType: 'FOOTER',
					isCommentsPanelOpen: isObjectSidebarShown && fg('confluence-frontend-comments-panel'),
					hasKeyPressed: true,
				});
			} else {
				handlePanelSelectionAndScroll({
					threadKey,
					isResolved: false,
					pageMode,
					setCurrentlySelectedThreadKey,
					eventEmitter: emitter,
					commentType: CommentType.GENERAL,
				});
			}
			setActiveHighlight(threadKey);
		},
		[
			emitter,
			pageMode,
			editorApi,
			setCurrentlySelectedThreadKey,
			setActiveHighlight,
			setEditorViewProps,
			isObjectSidebarShown,
		],
	);

	const getCommentIndex = useCallback(
		(currentMarkerRef: string): number => {
			return fg('confluence_frontend_content_wrapper')
				? [...orderedActiveAnnotations, ...footerCommentIds]?.findIndex(
						(markerRef) => markerRef === currentMarkerRef,
					)
				: orderedActiveAnnotations?.findIndex((markerRef) => markerRef === currentMarkerRef);
		},
		[orderedActiveAnnotations, footerCommentIds],
	);

	const handlePanelCommentNavigation = useCallback(
		(action: 'next' | 'previous') => {
			// If nothing is on the panel, there's nothing to navigate to
			if (currentlyRenderedThreads.length === 0) {
				return;
			}

			const { panelCommentRefs, navigableInlineCommentRefs } = parsePanelAndInlineRefs(
				currentlyRenderedThreads,
				orderedActiveAnnotations,
			);

			const nextRef = getNextRef(action, panelCommentRefs, currentlySelectedThreadKey);

			// Use unified keyboard shortcut navigation for panel navigation when feature is enabled
			if (fg('confluence_comments_unified_navigation')) {
				// Handle panel selection state management in the listener
				setCurrentlySelectedThreadKey(nextRef.ref);

				handleKeyboardShortcutNavigation({
					scrollBehavior: ScrollBehavior.SMOOTH,
					block: 'center',
					annotationId: nextRef.ref,
					commentType: nextRef.type === CommentType.GENERAL ? 'FOOTER' : 'INLINE',
					isCommentsPanelOpen: isObjectSidebarShown && fg('confluence-frontend-comments-panel'),
					hasKeyPressed: true,
					context: NavigationContext.EDITOR,
					isResolved: !navigableInlineCommentRefs.has(nextRef.ref),
				});
			} else {
				// Use old method when unified navigation is disabled
				handlePanelSelectionAndScroll({
					threadKey: nextRef.ref,
					isResolved: !navigableInlineCommentRefs.has(nextRef.ref),
					pageMode,
					setCurrentlySelectedThreadKey,
					eventEmitter: emitter,
					commentType: nextRef.type,
					scrollCommentsPanelToThread,
					shouldScrollCommentsPanel: true,
					hasKeyPressed: true,
				});
			}
		},
		[
			currentlyRenderedThreads,
			orderedActiveAnnotations,
			currentlySelectedThreadKey,
			pageMode,
			setCurrentlySelectedThreadKey,
			emitter,
			scrollCommentsPanelToThread,
			isObjectSidebarShown,
		],
	);

	const handleNextCommentNavigation = useCallback(
		({
			isNavigateToNextUnread,
			action,
		}: {
			isNavigateToNextUnread: boolean;
			action: 'next' | 'previous';
		}) => {
			//Check if the next inline comment is still in process of being rendered. Otherwise, quickly pressing keys will force comment to open and close instead of navigating
			if (isNextCommentLoading) return;

			setIsNextCommentLoading(true);

			navigateToInlineComment({
				action,
				triggeredByKeyboardShortcut: true,
				currentCommentIndex: getCommentIndex(activeHighlight || currentlySelectedThreadKey || ''),
				getCommentIndex,
				onNavigationClick,
				isEditor: true,
				removeCommentQueryParams,
				unresolvedInlineComments: orderedActiveAnnotations,
				createAnalyticsEvent,
				contentId,
				isNavigateToNextUnread,
				unreadInlineComments,
				unreadGeneralComments,
				footerComments: footerCommentIds,
				scrollToFooterComment,
				commentsDataMap,
			});
		},
		[
			activeHighlight,
			navigateToInlineComment,
			onNavigationClick,
			removeCommentQueryParams,
			orderedActiveAnnotations,
			createAnalyticsEvent,
			contentId,
			getCommentIndex,
			isNextCommentLoading,
			footerCommentIds,
			scrollToFooterComment,
			commentsDataMap,
			currentlySelectedThreadKey,
			unreadGeneralComments,
			unreadInlineComments,
		],
	);

	const handleStandaloneCommentNavigation = useCallback(
		(action: 'next' | 'previous') => {
			if (activeHighlight || currentlySelectedThreadKey) {
				// Check if we're navigating to a potentially unloaded footer comment
				const currentIndex = getCommentIndex(activeHighlight || currentlySelectedThreadKey || '');
				const totalComments = [...orderedActiveAnnotations, ...footerCommentIds].length;
				const navigatingToPotentiallyUnloadedFooterComment =
					isNavigatingToPotentiallyUnloadedFooterComment(
						action,
						currentIndex,
						totalComments,
						footerCommentsLoadingState,
					);

				if (
					pageMode === PageMode.LIVE &&
					navigatingToPotentiallyUnloadedFooterComment &&
					fg('confluence_frontend_kb_footer_loading')
				) {
					setPendingStandaloneAction(action);
				} else {
					handleNextCommentNavigation({
						isNavigateToNextUnread: false,
						action,
					});
				}
			} else {
				// No highlight selected, so we navigate to the first inline comment
				const firstInlineCommentMarkerRef = orderedActiveAnnotations[0];

				if (firstInlineCommentMarkerRef) {
					onNavigationClick(firstInlineCommentMarkerRef);
				} else if (pageMode === PageMode.LIVE && fg('confluence_frontend_content_wrapper')) {
					// Only Live pages have footer comments which is navigable
					if (
						fg('confluence_frontend_content_wrapper') ? footerCommentThreads.length === 0 : true
					) {
						// No active inline comments or footer comments found, but footer is not yet loaded - load footer comments and try again
						if (
							footerCommentsLoadingState === FooterCommentsLoadingState.NOT_LOADED &&
							fg('confluence_frontend_kb_footer_loading')
						) {
							setPendingStandaloneAction(action);
						}
					} else {
						scrollToFooterComment(footerCommentThreads[0]?.id);
					}
				}
			}
		},
		[
			activeHighlight,
			onNavigationClick,
			handleNextCommentNavigation,
			scrollToFooterComment,
			footerCommentThreads,
			pageMode,
			currentlySelectedThreadKey,
			orderedActiveAnnotations,
			footerCommentIds,
			footerCommentsLoadingState,
			getCommentIndex,
			setPendingStandaloneAction,
		],
	);

	const handleCommentNavigation = useCallback(
		(action: 'next' | 'previous') => {
			// Define behavior when the comments panel is shown - panel FG is a prereq for nav, so has to be checked first
			if (
				// eslint-disable-next-line confluence-feature-gating/no-preconditioning
				fg('confluence-frontend-comments-panel') &&
				isObjectSidebarShown &&
				panel?.id === PanelName.CommentsPanel
			) {
				handlePanelCommentNavigation(action);
			} else {
				// Comments panel not shown
				handleStandaloneCommentNavigation(action);
			}
		},
		[
			isObjectSidebarShown,
			panel?.id,
			handlePanelCommentNavigation,
			handleStandaloneCommentNavigation,
		],
	);

	useActiveCommentsQuery({
		pageId: contentId || '',
		skip:
			!triggeredByUnreadKeyboardShortcut ||
			alreadyFetchedInitialData ||
			expVal('cc_comments_new_comments_data_manager', 'isEnabled', false),
		onCompletedFn: () => {
			setAlreadyFetchedInitialData(true);
		},
	});

	// The flow is setPendingStandaloneAction --> load footer comments --> updates footerCommentIds --> retrigger KB shortcut with updated data
	const shouldLoadFooterComments = pendingStandaloneAction !== null && pageMode === PageMode.LIVE;

	useActiveCommentsQuery({
		pageId: contentId || '',
		skip:
			!shouldLoadFooterComments ||
			expVal('cc_comments_new_comments_data_manager', 'isEnabled', false),
		fetchPolicy: shouldLoadFooterComments ? 'network-only' : 'cache-only',
		type: 'FOOTER',
	});

	useEffect(() => {
		if (pendingStandaloneAction !== null) {
			handleStandaloneCommentNavigation(pendingStandaloneAction);
			setPendingStandaloneAction(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [footerCommentIds]);

	const handleNextUnreadNavigation = useCallback(() => {
		if (!alreadyFetchedInitialData) {
			//This will trigger the ActiveComments query if it's not already fetched
			setTriggeredByUnreadKeyboardShortcut(true);
			//Exit because the comment data won't be available yet to navigate to
			return;
		}

		if (unreadInlineComments.length + unreadGeneralComments.length === 0) {
			// No unread comments - don't do anything
			return;
		}

		//unread panel navigation
		if (
			isObjectSidebarShown &&
			panel?.id === PanelName.CommentsPanel &&
			fg('confluence-frontend-comments-panel')
		) {
			let nextThreadKey = '';
			let nextCommentType = CommentType.GENERAL;
			if (!currentlySelectedThreadKey) {
				if (unreadInlineComments.length > 0) {
					nextThreadKey = unreadInlineComments[0];
					nextCommentType = CommentType.INLINE;
				} else {
					nextThreadKey = unreadGeneralComments[0];
				}
			} else {
				nextThreadKey =
					getNextUnreadThreadKey(
						getCommentIndex(currentlySelectedThreadKey ?? '') + 1, //Add one otherwise it might get stuck on the current comment because there are unread replies which are collapsed
						[...orderedActiveAnnotations, ...footerCommentIds],
						[...unreadInlineComments, ...unreadGeneralComments],
					) ?? '';
				if (unreadInlineComments.includes(nextThreadKey)) {
					nextCommentType = CommentType.INLINE;
				}
			}

			// Use unified keyboard shortcut navigation for panel navigation when feature is enabled
			if (fg('confluence_comments_unified_navigation')) {
				// Handle panel selection state management in the listener
				setCurrentlySelectedThreadKey(nextThreadKey);

				handleKeyboardShortcutNavigation({
					scrollBehavior: ScrollBehavior.SMOOTH,
					block: 'center',
					annotationId: nextThreadKey,
					commentType: nextCommentType === CommentType.GENERAL ? 'FOOTER' : 'INLINE',
					isCommentsPanelOpen: isObjectSidebarShown && fg('confluence-frontend-comments-panel'),
					hasKeyPressed: true,
					context: NavigationContext.EDITOR,
					shortcutType: ShortcutType.UNREAD,
				});
			} else {
				// Use old method when unified navigation is disabled
				handlePanelSelectionAndScroll({
					threadKey: nextThreadKey,
					isResolved: false,
					pageMode,
					setCurrentlySelectedThreadKey,
					eventEmitter: emitter,
					commentType: nextCommentType,
					scrollCommentsPanelToThread,
					shouldScrollCommentsPanel: true,
				});
			}
			return;
		}

		if (activeHighlight || currentlySelectedThreadKey) {
			handleNextCommentNavigation({
				isNavigateToNextUnread: true,
				action: 'next',
			});
		} else {
			if (unreadInlineComments.length + unreadGeneralComments.length === 0) {
				return;
			}
			if (unreadInlineComments[0]) {
				onNavigationClick(unreadInlineComments[0]);
			} else {
				scrollToFooterComment(unreadGeneralComments[0]);
			}
		}
	}, [
		isObjectSidebarShown,
		panel?.id,
		handleNextCommentNavigation,
		onNavigationClick,
		activeHighlight,
		unreadInlineComments,
		unreadGeneralComments,
		currentlySelectedThreadKey,
		scrollToFooterComment,
		orderedActiveAnnotations,
		getCommentIndex,
		footerCommentIds,
		emitter,
		pageMode,
		setCurrentlySelectedThreadKey,
		scrollCommentsPanelToThread,
		alreadyFetchedInitialData,
	]);

	useEffect(() => {
		if (
			unreadInlineComments.length + unreadGeneralComments.length > 0 &&
			alreadyFetchedInitialData &&
			triggeredByUnreadKeyboardShortcut
		) {
			// This handles the case where user triggers unread keyboard shortcut when the comments data isn't fetched yet.
			// We need to wait for the data to be fetched before calling handleNextUnreadNavigation
			setTriggeredByUnreadKeyboardShortcut(false);

			// Putting this behind a timeout because need to wait for triggeredByUnreadKeyboardShortcut to be set to false otherwise got called multiple times
			setTimeout(() => {
				handleNextUnreadNavigation();
			}, 100);
		}
	}, [
		unreadInlineComments,
		unreadGeneralComments,
		handleNextUnreadNavigation,
		alreadyFetchedInitialData,
		triggeredByUnreadKeyboardShortcut,
		setTriggeredByUnreadKeyboardShortcut,
	]);

	const openCommentEditorShortcutCb = (e: KeyboardEvent, shortcut: string) => {
		e.preventDefault();

		if (canAddComments || !fg('confluence_fe_add_comment_kb_check_permission')) {
			if (
				document.querySelector('[data-testid="add-comment-media-button"]') &&
				fg('confluence_fe_create_inline_comment_exp_coverage')
			) {
				// This is the Comment button for media (image/video) objects in editor, which
				// handles keyboard shortcut separately. So just start the compound experience.
				createInlineCommentCompoundExperience.start({
					attributes: {
						pageClass: 'editor',
						commentType: 'block',
						blockType: 'media',
						entryPoint: 'keyboardShortcut',
					},
				});
				createInlineCommentCompoundExperience.initExperience.start();
				// Without this, initExperience will fail since it won't find a draft annotation.
				// So soft-fail it here to avoid failing the entire compound experience.
				createInlineCommentCompoundExperience.initExperience.softFail(
					new Error('Media in editor has no draft annotation as expected'),
				);
			} else if (
				document.querySelector('[data-testid="ak-editor-annotation-toolbar-create-button"]')
			) {
				handleCommentButtonClick({
					dialogs,
					shortcut,
					experienceAttributes: { entryPoint: 'keyboardShortcut' },
				});
			} else {
				// Check if we should show sticky draft instead of scrolling to object comment wrapper
				const isShowStickyDraft =
					!isObjectCommentWrapperVisible &&
					(addFooterCommentAdf || expanded) &&
					footerEditorEntry !== AddFooterCommentMode.STICKY_DRAFT &&
					fg('confluence_comment_sticky_footer_enabled');

				if (!expanded) {
					expandCommentContainer({ entryPoint: 'keyboardShortcutEditor' });
				}

			if (isShowStickyDraft || !isObjectCommentWrapperVisible) {
				setFooterEditorEntry(AddFooterCommentMode.STICKY_DRAFT);
			} else {
				document
					.querySelector(`[data-testid='object-comment-wrapper']`)
					?.scrollIntoView({ behavior: 'smooth' });
			}
			}
		}

		sendKeyboardShortcutAnalytics(shortcut);
	};

	return (
		<div data-testid="comments-shortcut-listener">
			{expVal('confluence_frontend_comments_a11y_kb_exp', 'isEnabled', false) && (
				<GeneralShortcutListener
					accelerator={UNIVERSAL_SELECT_COMMENT_SHORTCUT}
					listener={() => {
						const openComment =
							document.getElementById('editor-comments-sidebar') ||
							document.getElementById(`comment-thread-${currentlySelectedThreadKey}-container`);
						if (openComment) {
							openComment.setAttribute('tabIndex', '-1');
							openComment.focus();
						}
					}}
				/>
			)}
			<GeneralShortcutListener
				accelerator={UNIVERSAL_PREV_COMMENT_SHORTCUT}
				deprecatedAccelerator={viewMode ? PREV_COMMENT_SHORTCUT : undefined}
				listener={() => handleCommentNavigation('previous')}
			/>
			<GeneralShortcutListener
				accelerator={UNIVERSAL_NEXT_COMMENT_SHORTCUT}
				deprecatedAccelerator={viewMode ? NEXT_COMMENT_SHORTCUT : undefined}
				listener={() => handleCommentNavigation('next')}
			/>
			<GeneralShortcutListener
				accelerator={UNIVERSAL_NEXT_UNREAD_COMMENT_SHORTCUT}
				listener={handleNextUnreadNavigation}
			/>
			{fg('confluence_frontend_content_wrapper') && (
				<GeneralShortcutListener
					accelerator={UNIVERSAL_OPEN_COMMENT_EDITOR}
					listener={(e) => openCommentEditorShortcutCb(e, UNIVERSAL_OPEN_COMMENT_EDITOR)}
				/>
			)}
		</div>
	);
};

export const EditorCommentShortcutListener = withErrorBoundary({
	attribution: Attribution.COMMENTS,
})(withDialogs(EditorCommentShortcutListenerComponent));
